/**
 * Date utilities with timezone support
 */

// Jerusalem timezone
const JERUSALEM_TIMEZONE = 'Asia/Jerusalem';

/**
 * Convert a date to Jerusalem timezone
 * @param date Date to convert
 * @returns Date object representing the same moment in Jerusalem timezone
 */
const toJerusalemTime = (date: Date): Date => {
  // Get the timezone offset for Jerusalem at the given date
  const jerusalemTime = new Date(date.toLocaleString("en-US", {timeZone: JERUSALEM_TIMEZONE}));
  const utcTime = new Date(date.toLocaleString("en-US", {timeZone: "UTC"}));
  const offset = jerusalemTime.getTime() - utcTime.getTime();

  // Apply the offset to get the correct Jerusalem time
  return new Date(date.getTime() + offset);
};

/**
 * Format date and time in Jerusalem timezone or user's local timezone
 * @param date Date string or Date object
 * @param options Intl.DateTimeFormatOptions
 * @param useJerusalemTime Whether to use Jerusalem timezone (default: true)
 * @returns Formatted date string
 */
export const formatDateTime = (
  date: string | Date,
  options: Intl.DateTimeFormatOptions = {},
  useJerusalemTime: boolean = true
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (useJerusalemTime) {
    try {
      // Convert to Jerusalem timezone first
      const jerusalemDate = toJerusalemTime(dateObj);

      // Check if we explicitly want only date or only time
      const hasTimeOptions = options.hour !== undefined || options.minute !== undefined;
      const hasDateOptions = options.year !== undefined || options.month !== undefined || options.day !== undefined;

      const day = jerusalemDate.getDate().toString().padStart(2, '0');
      const month = (jerusalemDate.getMonth() + 1).toString().padStart(2, '0');
      const year = jerusalemDate.getFullYear();
      const hours = jerusalemDate.getHours().toString().padStart(2, '0');
      const minutes = jerusalemDate.getMinutes().toString().padStart(2, '0');

      if (hasDateOptions && !hasTimeOptions) {
        // Only date requested
        return `${day}/${month}/${year}`;
      } else if (!hasDateOptions && hasTimeOptions) {
        // Only time requested
        return `${hours}:${minutes}`;
      } else {
        // Default: show both date and time
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      }
    } catch (error) {
      console.warn('Jerusalem timezone conversion failed, using UTC:', error);
    }
  }

  // Fallback to regular formatting
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');

  // Check format options
  const hasTimeOptions = options.hour !== undefined || options.minute !== undefined;
  const hasDateOptions = options.year !== undefined || options.month !== undefined || options.day !== undefined;

  if (hasDateOptions && !hasTimeOptions) {
    // Only date requested
    return `${day}/${month}/${year}`;
  } else if (!hasDateOptions && hasTimeOptions) {
    // Only time requested
    return `${hours}:${minutes}`;
  } else {
    // Default: show both date and time
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }
};


/**
 * Format date only (no time) in Jerusalem timezone or user's local timezone
 * @param date Date string or Date object
 * @param useJerusalemTime Whether to use Jerusalem timezone (default: true)
 * @returns Formatted date string
 */
export const formatDate = (
  date: string | Date,
  useJerusalemTime: boolean = true
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (useJerusalemTime) {
    try {
      const jerusalemDate = toJerusalemTime(dateObj);
      const day = jerusalemDate.getDate().toString().padStart(2, '0');
      const month = (jerusalemDate.getMonth() + 1).toString().padStart(2, '0');
      const year = jerusalemDate.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.warn('Jerusalem date conversion failed, using UTC:', error);
    }
  }

  // Fallback to regular formatting
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Format time only (no date) in Jerusalem timezone or user's local timezone
 * @param date Date string or Date object
 * @param useJerusalemTime Whether to use Jerusalem timezone (default: true)
 * @returns Formatted time string
 */
export const formatTime = (
  date: string | Date,
  useJerusalemTime: boolean = true
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (useJerusalemTime) {
    try {
      const jerusalemDate = toJerusalemTime(dateObj);
      const hours = jerusalemDate.getHours().toString().padStart(2, '0');
      const minutes = jerusalemDate.getMinutes().toString().padStart(2, '0');

      return `${hours}:${minutes}`;
    } catch (error) {
      console.warn('Jerusalem time conversion failed, using UTC:', error);
    }
  }

  // Fallback to regular formatting
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
};

/**
 * Format date and time for meeting display
 * @param date Date string or Date object
 * @param useJerusalemTime Whether to use Jerusalem timezone (default: true)
 * @returns Formatted date and time string
 */
export const formatMeetingDateTime = (
  date: string | Date,
  useJerusalemTime: boolean = true
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (useJerusalemTime) {
    try {
      // Convert to Jerusalem timezone first, then format manually
      const jerusalemDate = toJerusalemTime(dateObj);

      const day = jerusalemDate.getDate().toString().padStart(2, '0');
      const month = (jerusalemDate.getMonth() + 1).toString().padStart(2, '0');
      const year = jerusalemDate.getFullYear();
      const hours = jerusalemDate.getHours().toString().padStart(2, '0');
      const minutes = jerusalemDate.getMinutes().toString().padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      console.warn('Jerusalem meeting datetime formatting failed, using fallback:', error);
    }
  }

  // Fallback to regular formatting
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Get timezone display name
 * @param useJerusalemTime Whether to use Jerusalem timezone (default: true)
 * @returns Timezone abbreviation or name
 */
export const getTimezoneDisplay = (useJerusalemTime: boolean = true): string => {
  if (!useJerusalemTime) {
    return 'Local Time';
  }

  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: JERUSALEM_TIMEZONE,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(now);
    const timeZoneName = parts.find(part => part.type === 'timeZoneName');
    return timeZoneName?.value || 'Jerusalem Time';
  } catch (error) {
    return 'Jerusalem Time';
  }
};

/**
 * Convert a datetime input (assumed to be in Jerusalem timezone) to UTC
 * @param datetimeLocal DateTime string from datetime-local input (YYYY-MM-DDTHH:mm)
 * @returns UTC date string suitable for API
 */
export const convertJerusalemInputToUTC = (datetimeLocal: string): string => {
  if (!datetimeLocal) return '';

  console.log('convertJerusalemInputToUTC input:', datetimeLocal);

  // Simple approach: treat the input as a Jerusalem time and convert to UTC
  // Add seconds to make it a complete datetime string
  const jerusalemDateTime = datetimeLocal + ':00';
  console.log('jerusalemDateTime with seconds:', jerusalemDateTime);

  // Create a date assuming it's in Jerusalem timezone
  // We'll construct an ISO string but interpret it correctly
  const tempDate = new Date(jerusalemDateTime);
  console.log('tempDate (browser local interpretation):', tempDate.toISOString());

  // Convert this date to what it would be in Jerusalem timezone
  const jerusalemString = tempDate.toLocaleString("en-US", {timeZone: JERUSALEM_TIMEZONE});
  console.log('jerusalemString:', jerusalemString);

  // Parse the Jerusalem string back to a date
  const jerusalemDate = new Date(jerusalemString);
  console.log('jerusalemDate parsed back:', jerusalemDate.toISOString());

  // Calculate the offset difference
  const offset = tempDate.getTime() - jerusalemDate.getTime();
  console.log('offset:', offset);

  // Apply the offset to get the correct UTC time
  const utcDate = new Date(tempDate.getTime() + offset);
  console.log('convertJerusalemInputToUTC result:', utcDate.toISOString());

  return utcDate.toISOString();
};

/**
 * Convert a UTC datetime to Jerusalem timezone for datetime-local input
 * @param utcDateString UTC datetime string
 * @returns Local datetime string in YYYY-MM-DDTHH:mm format for datetime-local input
 */
export const convertUTCToJerusalemInput = (utcDateString: string): string => {
  if (!utcDateString) return '';

  console.log('convertUTCToJerusalemInput input:', utcDateString);

  const utcDate = new Date(utcDateString);
  console.log('utcDate:', utcDate.toISOString());

  const jerusalemDate = toJerusalemTime(utcDate);
  console.log('jerusalemDate after conversion:', jerusalemDate.toISOString());

  // Format for datetime-local input (YYYY-MM-DDTHH:mm)
  // We need to get the local date/time components from the Jerusalem date
  const year = jerusalemDate.getFullYear();
  const month = (jerusalemDate.getMonth() + 1).toString().padStart(2, '0');
  const day = jerusalemDate.getDate().toString().padStart(2, '0');
  const hours = jerusalemDate.getHours().toString().padStart(2, '0');
  const minutes = jerusalemDate.getMinutes().toString().padStart(2, '0');

  const result = `${year}-${month}-${day}T${hours}:${minutes}`;
  console.log('convertUTCToJerusalemInput result:', result);

  return result;
};